import React, { useMemo, useState } from 'react'
import CheckboxOption from './CheckboxOption'
import PanelFooter from './PanelFooter'
import PanelHead from './PanelHead'
import useStagedSelection from '../useStagedSelection'
import buildCategoryTree, { normalizeHierMap } from '../buildCategoryTree'

// Reads category facets from `model.results.results.groups.categories`,
// applied state from `model.filters.departments.selected`, locked state
// from `model.filters.departments.locked`.
//
// Strict-hierarchy display per backend team:
// - The facets payload returns BOTH flat ("Sports") and hierarchical
//   ("Sports >> Basketball") keys for the same lot. Drop the flat
//   entries — display only the hierarchical ones, grouped as parent →
//   children. The parent label is the prefix; the child label is the
//   suffix.
// - Children are the only selectable rows. The staged/applied/URL value
//   for a child is its FULL hierarchical key ("Sports >> Basketball"),
//   not just the short name — different parents can share a child name
//   (e.g. "Memorabilia" under Sports, Music, Entertainment), and a
//   short-name-only key would make selecting one select/filter all of
//   them. The backend exact-matches the full pair.
// - The parent's visual checked state is derived from its children:
//   all-children-checked = parent checked. Clicking the parent toggles
//   all of its (non-locked) children at once.

export default function CategoryPanel({ model, changeState, search, onClose }) {
  const rawApplied = model.filters.departments.selected || {}
  const rawLocked = model.filters.departments.locked || {}
  const groups = (model.results && model.results.results && model.results.results.groups) || {}
  const facets = groups.categories || {}

  const applied = useMemo(() => normalizeHierMap(rawApplied), [rawApplied])

  const locked = useMemo(() => normalizeHierMap(rawLocked), [rawLocked])

  const [query, setQuery] = useState('')

  const { staged, toggle, setMany, reset, apply } = useStagedSelection({
    applied,
    locked,
    model,
    changeState,
    search,
    onClose,
    commit: (updated, stagedMap) => {
      // staged keys are already full "Parent >> Child" pairs — write through.
      updated.filters.departments.selected = { ...stagedMap }
      updated.filters.departments.on = false
    },
  })

  const tree = useMemo(() => buildCategoryTree(facets, rawLocked), [facets, rawLocked])

  const filteredTree = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tree
    const out = []
    for (const node of tree) {
      const parentMatches = node.name.toLowerCase().includes(q)
      const matchingKids = node.children.filter((c) =>
        c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q)
      )
      if (parentMatches || matchingKids.length > 0) {
        const kidsToShow = parentMatches && matchingKids.length === 0 ? node.children : matchingKids
        out.push({ ...node, children: kidsToShow })
      }
    }
    return out
  }, [tree, query])

  function parentChecked(node) {
    return node.children.length > 0 && node.children.every((c) => staged[c.name])
  }

  function toggleParent(node) {
    const allChecked = parentChecked(node)
    setMany((prev) => {
      const next = { ...prev }
      for (const child of node.children) {
        if (locked[child.name]) continue
        next[child.name] = !allChecked
      }
      return next
    })
  }

  return (
    <>
      <PanelHead title="Filter by Category" placeholder="Search categories" query={query} onQuery={setQuery} />
      <div className="fb-menu-list">
        {filteredTree.length === 0 && (
          <p className="fb-menu-empty">
            {query ? 'No categories match' : 'No categories available'}
          </p>
        )}
        {filteredTree.map((node) => (
          <div key={node.name} className="fb-menu-group">
            <CheckboxOption
              parent
              label={node.name}
              count={null}
              checked={parentChecked(node)}
              locked={false}
              onToggle={() => toggleParent(node)}
            />
            {node.children.map((child) => (
              <CheckboxOption
                key={child.name}
                child
                label={child.label}
                count={child.count}
                checked={!!staged[child.name]}
                locked={!!locked[child.name]}
                onToggle={() => toggle(child.name)}
              />
            ))}
          </div>
        ))}
      </div>
      <PanelFooter onReset={reset} onApply={apply} />
    </>
  )
}
